import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useUserAuth } from '../context/UserAuthContext';
import UserNav from './UserNav';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: var(--bg-color);
`;

const Sidebar = styled.aside`
  width: 250px;
  background-color: #1e3a8a;
  color: white;
  padding: 20px;
`;

const LogoContainer = styled.div`
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoImage = styled.img`
  width: 90px; /* Reduced by 50% from 180px */
  margin-bottom: 15px;
  border-radius: 50%; /* Make the logo round */
  object-fit: cover; /* Ensure the image fills the circular container */
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
  }
`;

const NavMenu = styled.nav`
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  li {
    margin-bottom: 10px;
  }
`;

const StyledNavLink = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 10px;
  color: #cbd5e1;
  text-decoration: none;
  border-radius: 5px;
  transition: all 0.2s;
  
  svg {
    margin-right: 10px;
  }
  
  &:hover {
    background-color: #2c4f9e;
    color: white;
  }
  
  &.active {
    background-color: #3b82f6;
    color: white;
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 20px;
  background-color: var(--bg-color);
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem 1.5rem;
  background-color: var(--card-bg);
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
`;

const Footer = styled.footer`
  padding: 1.5rem;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.875rem;
  border-top: 1px solid var(--border-color);
  margin-top: auto;
`;

const Layout = () => {
  const { user, loading } = useUserAuth();
  const navigate = useNavigate();

  // Redirect to login if not authenticated and not loading
  React.useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Will be redirected by the effect
  }

  return (
    <LayoutContainer>
      <Sidebar>
        <LogoContainer>
          <LogoImage src="/Logo.png" alt="AI Grower Logo" />
          <Logo>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="#3B82F6"/>
              <path d="M12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6ZM12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16Z" fill="#3B82F6"/>
            </svg>
            AI Grower
          </Logo>
        </LogoContainer>
        <NavMenu>
          <ul>
            <li>
              <StyledNavLink to="/">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 13H11V3H3V13ZM3 21H11V15H3V21ZM13 21H21V11H13V21ZM13 3V9H21V3H13Z" fill="currentColor"/>
                </svg>
                Dashboard
              </StyledNavLink>
            </li>
            <li>
              <StyledNavLink to="/zones/zone-1">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 6L9.78 11.63L11.03 13.3L14 9.33L19 16H10.54L6.53 10.63L1 18H23L14 6ZM5 16L6.52 13.97L8.04 16H5Z" fill="currentColor"/>
                </svg>
                Zones
              </StyledNavLink>
            </li>
            <li>
              <StyledNavLink to="/ask-agent">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z" fill="currentColor"/>
                </svg>
                Ask the Agent
              </StyledNavLink>
            </li>
            <li>
              <StyledNavLink to="/reports">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM9 17H7V10H9V17ZM13 17H11V7H13V17ZM17 17H15V13H17V17Z" fill="currentColor"/>
                </svg>
                Reports & Trends
              </StyledNavLink>
            </li>
          </ul>
        </NavMenu>
      </Sidebar>
      <MainContent>
        <Header>
          <UserNav />
        </Header>
        <ContentWrapper>
          <Outlet />
        </ContentWrapper>
        <Footer>
          © 2025 AI Grower Assistant. iEcosystems All rights reserved
        </Footer>
      </MainContent>
    </LayoutContainer>
  );
};

export default Layout;
